<template>
  <div class="ee">
    <div class="ee-top">
      <span :id="id + '-label'" class="pb-label ee-title">Shorten the quote</span>
      <div class="pb-segmented" role="group" :aria-labelledby="id + '-label'">
        <button type="button" :aria-pressed="mode === 'sentences' ? 'true' : 'false'" @click="mode = 'sentences'">Pick sentences</button>
        <button type="button" :aria-pressed="mode === 'words' ? 'true' : 'false'" @click="startWords">Remove words</button>
      </div>
    </div>
    <p class="pb-help ee-help">Shortening can only remove words. Anything removed shows as "…", and the original stays one click away.</p>

    <div v-if="mode === 'sentences'" class="ee-sentences" role="group" aria-label="Sentences in the quote">
      <button v-for="(part, index) in parts" :key="index" type="button" :class="['ee-part', { off: !kept[index] }]"
              :aria-pressed="kept[index] ? 'true' : 'false'" @click="toggle(index)">
        <i :class="['mdi', kept[index] ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline']" aria-hidden="true"></i>
        <span>{{ part.trim() }}</span>
      </button>
    </div>

    <div v-else>
      <label class="pb-sr-only" :for="id + '-words'">Quote text</label>
      <textarea :id="id + '-words'" v-model="words" :class="['pb-textarea', { invalid: !!wordError }]" rows="5"
                :aria-describedby="id + '-words-help'" :aria-invalid="wordError ? 'true' : null"></textarea>
      <span :id="id + '-words-help'" class="pb-help">Delete words, and type … (or three dots) where you removed something.</span>
      <span v-if="wordError" class="pb-field-error" role="alert">{{ wordError }}</span>
    </div>

    <div class="pb-actions ee-actions">
      <button type="button" class="pb-button ghost sm" :disabled="!result" @click="reset">Use the full answer</button>
    </div>
  </div>
</template>

<script>
const SPLIT = /\s*(?:…|\.\.\.)\s*/;
const ELLIPSIS = '…';
let counter = 0;

// The same rule the server applies (serializers.validate_excerpt): every fragment between "…"
// markers must appear, in order, in the original. Returns {excerpt, error}; excerpt null = full text.
export function checkExcerpt(original, text) {
  const value = String(text || '').trim();
  const source = String(original || '');
  if (!value) return { excerpt: null, error: '' };
  const fragments = value.split(SPLIT).map(part => part.trim()).filter(Boolean);
  if (!fragments.length) return { excerpt: null, error: 'Keep at least a few of the customer\'s words.' };
  let position = 0;
  let first = -1;
  for (const fragment of fragments) {
    const index = source.indexOf(fragment, position);
    if (index < 0) return { excerpt: null, error: 'A quote can only be shortened: remove words and mark each removal with "…". Nothing can be added or changed.' };
    if (first < 0) first = index;
    position = index + fragment.length;
  }
  let result = fragments.join(' ' + ELLIPSIS + ' ');
  if (source.slice(0, first).trim()) result = ELLIPSIS + ' ' + result;
  if (source.slice(position).trim()) result = result + ' ' + ELLIPSIS;
  return { excerpt: result === source.trim() ? null : result, error: '' };
}

function sentences(text) {
  const parts = String(text || '').match(/[^.!?…\n]+(?:[.!?…]+["'”’)]*)?|\n+/g) || [];
  return parts.filter(part => part.trim());
}

export default {
  name: 'ExcerptEditor',
  props: {
    original: { type: String, default: '' },
    value: { type: String, default: null }
  },
  data() {
    counter += 1;
    return { id: 'excerpt-' + counter, mode: 'sentences', kept: [], words: '' };
  },
  computed: {
    parts() { return sentences(this.original); },
    fromSentences() {
      if (!this.parts.length || this.kept.every(Boolean)) return null;
      const pieces = [];
      let gap = false;
      this.parts.forEach((part, index) => {
        if (this.kept[index]) {
          if (gap && pieces.length) pieces.push(ELLIPSIS);
          pieces.push(part.trim());
          gap = false;
        } else {
          gap = true;
        }
      });
      if (!pieces.length) return '';
      return checkExcerpt(this.original, pieces.join(' ')).excerpt;
    },
    checked() { return checkExcerpt(this.original, this.words); },
    wordError() { return this.mode === 'words' ? this.checked.error : ''; },
    result() { return this.mode === 'sentences' ? this.fromSentences : this.checked.excerpt; },
    valid() {
      if (this.mode === 'sentences') return this.fromSentences !== '';
      return !this.checked.error && !!this.words.trim();
    }
  },
  watch: {
    original: { immediate: true, handler() { this.load(); } },
    value() { if (this.value !== this.result) this.load(); },
    result(value) { if (this.valid) this.$emit('input', value); },
    valid: { immediate: true, handler(value) { this.$emit('valid', value); } }
  },
  methods: {
    load() {
      const excerpt = this.value || '';
      this.kept = this.parts.map(part => !excerpt || excerpt.indexOf(part.trim()) >= 0);
      this.words = excerpt || this.original;
      if (excerpt && this.fromSentences !== excerpt) this.mode = 'words';
    },
    toggle(index) {
      const next = this.kept.slice();
      next[index] = !next[index];
      if (!next.some(Boolean)) return;
      this.kept = next;
    },
    startWords() {
      this.words = this.result || this.original;
      this.mode = 'words';
    },
    reset() {
      this.kept = this.parts.map(() => true);
      this.words = this.original;
    }
  }
};
</script>

<style scoped>
.ee-top{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:10px}
.ee-title{margin:0}
.ee-help{margin:8px 0 12px}
.ee-sentences{display:flex;flex-direction:column;gap:6px}
.ee-part{display:flex;gap:10px;align-items:flex-start;text-align:left;width:100%;min-height:44px;padding:10px 12px;border:1px solid #E6DED2;
  border-radius:10px;background:#fff;color:#1F2328;font:400 14.5px/1.5 var(--font-body);cursor:pointer}
.ee-part:hover{border-color:#D4C9B8;background:#FBF8F3}
.ee-part .mdi{color:#B4472B;font-size:18px;line-height:22px}
.ee-part.off{background:#F3EEE6;color:#6B635B;text-decoration:line-through;text-decoration-color:#8C8378}
.ee-part.off .mdi{color:#8C8378}
.ee-actions{margin-top:10px}
</style>
